import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { OfflineBanner } from '@/components/OfflineBanner'
import { PullToRefresh } from '@/components/PullToRefresh'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { LoginPage } from '@/pages/LoginPage'
import { TripsPage } from '@/pages/TripsPage'
import { ArchivedTripsPage } from '@/pages/ArchivedTripsPage'
import { NewTripPage } from '@/pages/NewTripPage'
import { EditTripPage } from '@/pages/EditTripPage'
import { ItineraryPage } from '@/pages/ItineraryPage'
import { MembersPage } from '@/pages/MembersPage'
import { BookingsPage } from '@/pages/BookingsPage'
import { MapPage } from '@/pages/MapPage'
import { SuggestionsPage } from '@/pages/SuggestionsPage'
import { ProposalsPage } from '@/pages/ProposalsPage'
import { BudgetPage } from '@/pages/BudgetPage'
import { ChecklistPage } from '@/pages/ChecklistPage'
import { DocumentsPage } from '@/pages/DocumentsPage'
import { SharedTripPage } from '@/pages/SharedTripPage'
import { useAuthSession } from '@/hooks/useAuthSession'
import { queryClient } from '@/lib/queryClient'

function AppRoutes() {
  const { isLoading } = useAuthSession()

  if (isLoading) return null

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/share/:token" element={<SharedTripPage />} />
      <Route
        path="/trips"
        element={
          <ProtectedRoute>
            <TripsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/archived"
        element={
          <ProtectedRoute>
            <ArchivedTripsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/new"
        element={
          <ProtectedRoute>
            <NewTripPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/:tripId/edit"
        element={
          <ProtectedRoute>
            <EditTripPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/:tripId/itinerary"
        element={
          <ProtectedRoute>
            <ItineraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/:tripId/members"
        element={
          <ProtectedRoute>
            <MembersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/:tripId/bookings"
        element={
          <ProtectedRoute>
            <BookingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/:tripId/map"
        element={
          <ProtectedRoute>
            <MapPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/:tripId/suggestions"
        element={
          <ProtectedRoute>
            <SuggestionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/:tripId/proposals"
        element={
          <ProtectedRoute>
            <ProposalsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/:tripId/budget"
        element={
          <ProtectedRoute>
            <BudgetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/:tripId/checklist"
        element={
          <ProtectedRoute>
            <ChecklistPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips/:tripId/documents"
        element={
          <ProtectedRoute>
            <DocumentsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/trips" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <OfflineBanner />
          <PullToRefresh>
            <AppRoutes />
          </PullToRefresh>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
